import React from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAmoebaConfig } from '../hooks/useAmoebaConfig';
import { routeAllows } from '../navigation/modules';

// Before joining, people only get their joining portal.
const PORTAL_PAGES = ['/welcome', '/tasks', '/documents'];

/** The page belongs to a module the organisation has not switched on: say so, rather than show an empty page. */
const ModuleOff = () => (
    <div className="container" style={{ padding: '3rem 1rem', maxWidth: 640 }}>
        <h1 style={{ marginTop: 0 }}>Not switched on</h1>
        <p style={{ color: 'var(--text-muted)' }}>
            This part of the app is not switched on for your organisation. Ask your administrator if you need it.
        </p>
        <Link to="/dashboard">Back to Home</Link>
    </div>
);

/**
 * `rule`: the page's entry in navigation/modules.js ROUTES ({ roles, amoeba }). A wrong role goes to Home; a module
 * that is switched off shows a short message. `requiredRole` / `requireSystemAdmin` remain for the routes outside it.
 */
const ProtectedRoute = ({ children, requiredRole, requireSystemAdmin, rule }) => {
    const location = useLocation();
    const { loading, hasAmoeba } = useAmoebaConfig();
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    if (!isAuthenticated) {
        // Sign in, then come back to this page (the sign-in page checks the value).
        const next = encodeURIComponent(location.pathname + location.search + location.hash);
        return <Navigate to={`/login?next=${next}`} replace />;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (user.status === 'PRE_JOINING' && !PORTAL_PAGES.includes(location.pathname) && !location.pathname.startsWith('/help')) {
        return <Navigate to="/welcome" replace />;
    }

    if (requireSystemAdmin && !user.isSystemAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    if (requiredRole) {
        const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        if (!roles.includes(user.role)) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    if (rule) {
        if (!routeAllows({ roles: rule.roles }, user)) return <Navigate to="/dashboard" replace />;
        if (rule.amoeba) {
            if (loading) return null; // the organisation's modules are still loading
            if (!hasAmoeba(rule.amoeba)) return <ModuleOff />;
        }
    }

    // Supports both <ProtectedRoute><SomePage /></ProtectedRoute>
    // and <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}> (Outlet via children)
    return children ?? <Outlet />;
};

export default ProtectedRoute;
